import { LitElement, html, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { rmaDiscardModal } from './discard-modal.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { type CSEModal } from '@cloudsuite/mosaic-elements'
import { deleteRmaContext } from 'components/ordering/rma/components/context/rma-context.js'
import { RmaError } from 'components/ordering/rma/api/dtos/error.js'

const translations = {
  header: () => t('header', { ns: 'csc-rma-discard-modal', default: 'Cancel return request' }),
  body: () => t('body', { ns: 'csc-rma-discard-modal', default: 'Are you sure you want to cancel this return request?' }),
  cancel: () => t('cancel', { ns: 'csc-rma-discard-modal', default: 'No, go back' }),
  confirm: () => t('confirm', { ns: 'csc-rma-discard-modal', default: 'Yes, cancel' }),
  error: () => t('error', { ns: 'csc-rma-discard-modal', default: 'Deleting your return request failed. Please try again.' }),
}

@customElement('csc-rma-discard-modal')
export class CSCRmaDiscardModal extends LitElement {
  static override styles = [hostStyle, rmaDiscardModal]

  @query('cse-modal')
  private modal!: CSEModal

  @property({ attribute: 'discard-url', type: String })
  public discardUrl = ''

  @inject(WINDOW_LOCATION)
  private windowLocation: WindowLocation | undefined

  @consume({ context: deleteRmaContext, subscribe: true })
  private deleteRma: (() => Promise<void>) | null = null

  @state() private discarding = false
  @state() private error: string | ReturnType<typeof t> | null = null

  public open() {
    this.modal?.open()
  }

  public close() {
    this.modal?.close()
  }

  private async onConfirm() {
    if (!this.deleteRma) return
    this.discarding = true
    this.error = null
    try {
      await this.deleteRma()
      if (this.discardUrl) {
        this.windowLocation?.redirect(new URL(this.discardUrl, window.location.origin))
      }
    } catch (error) {
      this.error = error instanceof RmaError ? error.message : translations.error()
    } finally {
      this.discarding = false
    }
  }

  override render() {
    return html`
      <cse-modal id="discard-modal" size="default" variant="error">
        <div slot="header">${translations.header()}</div>
        <cse-icon slot="icon" icon-name="trash-can" icon-style="light" size="xl"></cse-icon>
        <cse-text>${translations.body()}</cse-text>
        ${this.error ? html`<cse-text role="alert" class="rma-discard-error">${this.error}</cse-text>` : nothing}
        <cse-button
          slot="footer"
          size="small"
          fit="fill"
          ?disabled="${this.discarding}"
          @click="${() => this.close()}"
        >
          ${translations.cancel()}
        </cse-button>
        <cse-button
          slot="footer"
          size="small"
          fit="fill"
          ?disabled="${this.discarding}"
          @click="${() => this.onConfirm()}"
        >
          ${translations.confirm()}
        </cse-button>
      </cse-modal>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma-discard-modal': CSCRmaDiscardModal
  }
}
